/** Modal dialog for adding/editing a supplier. */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  Typography,
  InputAdornment,
} from "@mui/material";

export interface SupplierData {
  id?: string;
  name: string;
  hostname: string;
  status: "ACTIVE" | "DISABLED" | "SUSPENDED";
  capacity_cps: number;
  capacity_cc: number;
  priority: number;
  cgr_subject?: string;
  tariff_plan: string;
}

interface SupplierFormProps {
  open: boolean;
  onClose: () => void;
  supplier: SupplierData | null;
  onSave: (supplier: SupplierData) => Promise<void>;
}

/** Supplier form modal with CGRates integration fields. */
export default function SupplierForm({
  open,
  onClose,
  supplier,
  onSave,
}: SupplierFormProps) {
  const [name, setName] = useState("");
  const [hostname, setHostname] = useState("");
  const [status, setStatus] = useState<"ACTIVE" | "DISABLED" | "SUSPENDED">("ACTIVE");
  const [capacityCps, setCapacityCps] = useState("0");
  const [capacityCc, setCapacityCc] = useState("0");
  const [priority, setPriority] = useState("100");
  const [cgrSubject, setCgrSubject] = useState("");
  const [tariffPlan, setTariffPlan] = useState("default");

  const [nameError, setNameError] = useState("");
  const [hostnameError, setHostnameError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /** Reset form to default or supplier data. */
  useEffect(() => {
    if (supplier) {
      setName(supplier.name);
      setHostname(supplier.hostname);
      setStatus(supplier.status);
      setCapacityCps(supplier.capacity_cps.toString());
      setCapacityCc(supplier.capacity_cc.toString());
      setPriority(supplier.priority.toString());
      setCgrSubject(supplier.cgr_subject || "");
      setTariffPlan(supplier.tariff_plan);
    } else {
      setName("");
      setHostname("");
      setStatus("ACTIVE");
      setCapacityCps("0");
      setCapacityCc("0");
      setPriority("100");
      setCgrSubject("");
      setTariffPlan("default");
    }
    setNameError("");
    setHostnameError("");
    setError("");
  }, [supplier, open]);

  /** Generate CGR subject from name when name changes and subject is empty. */
  useEffect(() => {
    if (name && !cgrSubject && !supplier?.cgr_subject) {
      const generated = "CR_" + name.replace(/\s+/g, "_").toUpperCase();
      setCgrSubject(generated);
    }
  }, [name, cgrSubject, supplier]);

  /** Validate name input. */
  const validateName = (value: string): boolean => {
    if (value.length < 2) {
      setNameError("Name must be at least 2 characters");
      return false;
    }
    setNameError("");
    return true;
  };

  /** Validate hostname input. */
  const validateHostname = (value: string): boolean => {
    if (!value || value.trim().length === 0) {
      setHostnameError("Hostname is required");
      return false;
    }
    setHostnameError("");
    return true;
  };

  /** Handle form submission. */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isNameValid = validateName(name);
    const isHostnameValid = validateHostname(hostname);

    if (!isNameValid || !isHostnameValid) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      await onSave({
        id: supplier?.id,
        name,
        hostname,
        status,
        capacity_cps: parseInt(capacityCps, 10),
        capacity_cc: parseInt(capacityCc, 10),
        priority: parseInt(priority, 10),
        cgr_subject: cgrSubject || undefined,
        tariff_plan: tariffPlan,
      });
      onClose();
    } catch (err: any) {
      console.error("Failed to save supplier:", err);
      setError(
        err.response?.data?.detail || "Failed to save supplier. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /** Handle dialog close. */
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const isFormValid =
    name.length >= 2 &&
    !nameError &&
    hostname.length > 0 &&
    !hostnameError;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{supplier ? "Edit Supplier" : "Add Supplier"}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            margin="dense"
            label="Supplier Name"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (e.target.value.length > 0) validateName(e.target.value);
            }}
            error={!!nameError}
            helperText={nameError || "Friendly name for this supplier"}
            disabled={loading}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Hostname"
            fullWidth
            variant="outlined"
            value={hostname}
            onChange={(e) => {
              setHostname(e.target.value);
              if (e.target.value.length > 0) validateHostname(e.target.value);
            }}
            error={!!hostnameError}
            helperText={hostnameError || "SIP hostname or IP address"}
            disabled={loading}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Billing Subject"
              fullWidth
              variant="outlined"
              value={cgrSubject}
              onChange={(e) => setCgrSubject(e.target.value)}
              disabled={loading}
              helperText="Billing subject (auto-generated from name)"
            />
            <TextField
              margin="dense"
              label="Tariff Plan"
              fullWidth
              variant="outlined"
              value={tariffPlan}
              onChange={(e) => setTariffPlan(e.target.value)}
              disabled={loading}
              helperText="Billing engine tariff plan for LCR"
            />
          </Box>

          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as any)}
              disabled={loading}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="DISABLED">Disabled</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Max CPS (0=unlimited)"
              type="number"
              variant="outlined"
              value={capacityCps}
              onChange={(e) => setCapacityCps(e.target.value)}
              disabled={loading}
              fullWidth
              inputProps={{ min: 0 }}
            />
            <TextField
              margin="dense"
              label="Max Concurrent Calls (0=unlimited)"
              type="number"
              variant="outlined"
              value={capacityCc}
              onChange={(e) => setCapacityCc(e.target.value)}
              disabled={loading}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Box>

          <TextField
            margin="dense"
            label="Priority"
            type="number"
            variant="outlined"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            disabled={loading}
            fullWidth
            inputProps={{ min: 0 }}
            helperText="Lower number = higher priority (default: 100)"
          />

          <Box sx={{ mt: 2, p: 1.5, bgcolor: "info.lighter", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Note:</strong> Gateway endpoints are managed separately after creating the supplier.
              Click "Manage Gateways" to add/edit SIP endpoints for this supplier.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isFormValid || loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? "Saving..." : supplier ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
